import React, { useMemo } from 'react';
import { AccessorKeyColumnDef, createColumnHelper, RowData } from '@tanstack/react-table';
import { CourseDataEntry } from '../CourseAgenda/CourseAgendaOverview';
import { ActionCell, DateCell, ExpanderCell, ReservationCell } from '../TableCells';
import { compareDates } from '../../../../utils/sort';
import moment from '../../../../moment';
import { textFilter } from '../../Filters/TextFilter';
import { CODE_95_ENABLED, useViewPreferences } from '../../../../hooks/view-preferences';
import { useTranslation } from 'react-i18next';
import { findExistingReservation, getCalculatedFreeCapacity } from '../../../../utils/training';
import { useUser } from '../../../../hooks/use-user';
import Table from './Table';

type Props =
	| {
			data: CourseDataEntry[];
			handleRowClick: (row: RowData) => void;
			isFuturePlanningList: true;
			onEnrollClick: (row: RowData) => void;
			onReserveClick: (row: RowData) => void;
			prepareDownload?: never;
	  }
	| {
			data: CourseDataEntry[];
			handleRowClick: (row: RowData) => void;
			isFuturePlanningList?: false | null | undefined;
			onEnrollClick?: never;
			onReserveClick?: never;
			prepareDownload?: () => void;
	  };

const columnHelper = createColumnHelper<CourseDataEntry>();

const PlanningOverview = ({
	data,
	isFuturePlanningList,
	onEnrollClick,
	onReserveClick,
	handleRowClick,
	prepareDownload,
}: Props) => {
	const { activeCompany } = useUser();
	const { isPreferenceOptionEnabled } = useViewPreferences();
	const code95Enabled = isPreferenceOptionEnabled(CODE_95_ENABLED);
	const { t } = useTranslation();

	const columns = useMemo(
		() =>
			[
				columnHelper.display({
					id: 'expander',
					cell: ExpanderCell,
					enableColumnFilter: false,
				}),
				columnHelper.accessor('start', {
					header: t('trainings.date'),
					cell: DateCell,
					sortingFn: (rowA, rowB) =>
						compareDates(moment(rowA.original.start), moment(rowB.original.start)),
					enableColumnFilter: false,
				}),
				...(code95Enabled
					? [
							columnHelper.accessor('ccvCode', {
								header: t('trainings.ccvCode'),
								filterFn: textFilter,
								meta: {
									filterVariant: 'text',
								},
							}),
						]
					: []),
				columnHelper.accessor('name', {
					header: t('trainings.training'),
					filterFn: textFilter,
					meta: {
						filterVariant: 'text',
					},
				}),
				columnHelper.accessor('city', {
					header: t('trainings.location'),
					filterFn: textFilter,
					meta: {
						filterVariant: 'text',
					},
				}),
				columnHelper.accessor('trainingReservations', {
					header: t('trainings.reservations'),
					cell: ReservationCell,
				}),
				...(isFuturePlanningList
					? [
							columnHelper.accessor('freeCapacity', {
								header: t('trainings.capacity'),
								cell: (cellInfo) => {
									if (!cellInfo.row.original.freeCapacity) {
										return null;
									}
									const existingReservation =
										(cellInfo.row.original.trainingReservations &&
											findExistingReservation(
												cellInfo.row.original.trainingReservations,
												activeCompany.id,
											)) ||
										{};

									return getCalculatedFreeCapacity(
										cellInfo.row.original.freeCapacity,
										existingReservation,
									);
								},
							}),
							columnHelper.display({
								id: 'actions',
								cell: (cellInfo) => (
									<ActionCell
										{...cellInfo}
										onEnrollClick={onEnrollClick}
										onReserveClick={onReserveClick}
									/>
								),
								enableColumnFilter: false,
							}),
						]
					: []),
			] as Array<AccessorKeyColumnDef<Partial<CourseDataEntry>, unknown>>,
		[isFuturePlanningList, code95Enabled, t],
	);

	return (
		<Table
			data={data}
			columns={columns}
			handleRowClick={handleRowClick}
			prepareDownload={prepareDownload}
		/>
	);
};

export default PlanningOverview;
